/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useEffect } from 'react';
import * as styles from '../styles/components/project-view.css.ts';

interface ResizerProps {
  leftRef: React.RefObject<any>;
  rightRef: React.RefObject<any>;
  minLeftWidth?: number;
  minRightWidth?: number;
}

export function Resizer({ 
  leftRef, 
  rightRef, 
  minLeftWidth = 300, 
  minRightWidth = 300 
}: ResizerProps) {
  const resizerRef = useRef<any>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    // 初期サイズを設定
    if (leftRef.current && rightRef.current) {
      const container = leftRef.current.parentElement;
      if (container) {
        const containerWidth = container.getBoundingClientRect().width;
        const resizerWidth = 4;
        const availableWidth = containerWidth - resizerWidth;
        
        // 初期状態では 70% : 30% の比率で分割
        const initialLeftWidth = Math.floor(availableWidth * 0.7);
        const initialRightWidth = availableWidth - initialLeftWidth;
        
        leftRef.current.style.flex = 'none';
        leftRef.current.style.width = `${initialLeftWidth}px`;
        
        rightRef.current.style.flex = 'none';
        rightRef.current.style.width = `${initialRightWidth}px`;
      }
    }

    const handleMouseDown = (e: any) => {
      if (!leftRef.current || !rightRef.current) return;
      
      isDragging.current = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      e.preventDefault();
    };

    const handleMouseMove = (e: any) => {
      if (!isDragging.current || !leftRef.current || !rightRef.current) return;
      
      const container = leftRef.current.parentElement;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      
      // リサイザーの幅（4px）を考慮した計算
      const resizerWidth = 4;
      const availableWidth = containerRect.width - resizerWidth;
      
      const maxLeftWidth = availableWidth - minRightWidth;
      const newLeftWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, mouseX));
      const newRightWidth = availableWidth - newLeftWidth;
      
      // flexベースの幅設定に変更
      leftRef.current.style.flex = 'none';
      leftRef.current.style.width = `${newLeftWidth}px`;
      
      rightRef.current.style.flex = 'none';
      rightRef.current.style.width = `${newRightWidth}px`;
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const handleWindowResize = () => {
      if (leftRef.current && rightRef.current) {
        const container = leftRef.current.parentElement;
        if (container) {
          const containerWidth = container.getBoundingClientRect().width;
          const resizerWidth = 4;
          const availableWidth = containerWidth - resizerWidth;
          
          const currentLeftWidth = leftRef.current.getBoundingClientRect().width;
          const currentRightWidth = rightRef.current.getBoundingClientRect().width;
          const totalCurrentWidth = currentLeftWidth + currentRightWidth;
          
          if (totalCurrentWidth > 0) {
            // 現在の比率を維持してリサイズ
            const leftRatio = currentLeftWidth / totalCurrentWidth;
            const newLeftWidth = Math.max(minLeftWidth, Math.min(availableWidth - minRightWidth, availableWidth * leftRatio));
            const newRightWidth = availableWidth - newLeftWidth;
            
            leftRef.current.style.width = `${newLeftWidth}px`;
            rightRef.current.style.width = `${newRightWidth}px`;
          }
        }
      }
    };

    const resizer = resizerRef.current;
    if (resizer) {
      resizer.addEventListener('mousedown', handleMouseDown);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      if (resizer) {
        resizer.removeEventListener('mousedown', handleMouseDown);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [leftRef, rightRef, minLeftWidth, minRightWidth]);

  return (
    <div 
      ref={resizerRef}
      className={styles.resizer}
      data-testid="resizer"
      title="ドラッグしてサイズを調整"
    />
  );
}
